import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react'
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Heading,
  HStack,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Select,
  Tabs,
  TabList,
  Tab,
  Badge,
  Text,
  Textarea,
  useDisclosure,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalCloseButton,
  VStack,
} from '@chakra-ui/react'
import { Plus, Search } from 'lucide-react'
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useAccessCheck } from '@/hooks/useAccess'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

type TabValue = 'all' | 'trash'

type OptionGroup = {
  id: number
  group_code: string
  group_label: string
  description: string | null
  locked: boolean
}

type OptionValueRow = {
  id?: number
  code: string
  label: string
  sort_order: number
  active: boolean
}

const emptyValue: OptionValueRow = { code: '', label: '', sort_order: 0, active: true }
const emptyGroup = { group_code: '', group_label: '', description: '' }

/**
 * Single-page admin tool for the generic "Ticket Option Lists" system —
 * replaces the small hardcoded arrays previously inlined in
 * ParentTicketForm / SlaForm / CreateTicketRequestModal / CreateTicketModal /
 * TicketFormTriageSection (severity, billing type, request source, and the
 * triage Yes/No/N/A sets). See TicketOptionListSeeder for the full mapping.
 *
 * Left: pick a group (or add a new one). Right: manage that group's values
 * in a normal DataTable, with a small modal for add/edit instead of a
 * separate route — these lists are short, so a full page per value would be
 * overkill.
 */
export default function TicketOptionListsPage() {
  const colors = useThemeColors()
  const tableRef = useRef<DataTableRef>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const { showError, showSuccess, showWarning } = useToastNotification()

  const [groups, setGroups] = useState<OptionGroup[]>([])
  const [groupsLoading, setGroupsLoading] = useState(true)
  const [selectedGroupId, setSelectedGroupId] = useState<number | ''>('')

  const [activeTab, setActiveTab] = useState<TabValue>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [totalRows, setTotalRows] = useState(0)
  const [totalTrash, setTotalTrash] = useState(0)
  const [bulkAction, setBulkAction] = useState('')
  const [isBulkProcessing, setIsBulkProcessing] = useState(false)
  const [confirmMessage, setConfirmMessage] = useState('')

  const access = useAccessCheck()

  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure()
  const { isOpen: isValueModalOpen, onOpen: onValueModalOpen, onClose: onValueModalClose } = useDisclosure()
  const { isOpen: isGroupModalOpen, onOpen: onGroupModalOpen, onClose: onGroupModalClose } = useDisclosure()

  const [valueForm, setValueForm] = useState<OptionValueRow>(emptyValue)
  const [valueErrors, setValueErrors] = useState<Record<string, string[]>>({})
  const [savingValue, setSavingValue] = useState(false)

  const [groupForm, setGroupForm] = useState(emptyGroup)
  const [groupErrors, setGroupErrors] = useState<Record<string, string[]>>({})
  const [savingGroup, setSavingGroup] = useState(false)

  const loadGroups = () => {
    setGroupsLoading(true)
    apiClient
      .get('/service-catalog/ticket-option-lists', { params: { per_page: 100 } })
      .then((res) => {
        const rows: OptionGroup[] = Array.isArray(res.data?.data) ? res.data.data : []
        setGroups(rows)
        if (rows.length > 0 && selectedGroupId === '') {
          setSelectedGroupId(rows[0].id)
        }
      })
      .catch((e) => showError(e))
      .finally(() => setGroupsLoading(false))
  }

  useEffect(() => {
    loadGroups()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const selectedGroup = useMemo(() => groups.find((g) => g.id === selectedGroupId) ?? null, [groups, selectedGroupId])

  const tableOptions: DataTableOptions = useMemo(
    () => ({
      showActionsColumn: true,
      dataFields: {
        code: { name: 'Code', withSort: true, maxWidth: '160px' },
        label: { name: 'Label', withSort: true, maxWidth: '220px' },
        sort_order: { name: 'Sort', withSort: true, maxWidth: '80px' },
        active: {
          name: 'Status',
          withSort: true,
          maxWidth: '100px',
          badge: { true: 'green', false: 'gray' },
          badgeLabels: { true: 'Active', false: 'Inactive' },
        },
      },
      dataSource:
        activeTab === 'trash' ? '/service-catalog/ticket-option-values/archived' : '/service-catalog/ticket-option-values',
      primaryKey: 'id',
      softDelete: true,
      displayInModal: true,
      noRecordText: 'No values yet',
      hideSelection: false,
      otherActions: [],
    }),
    [activeTab]
  )

  const permissions = useMemo(
    () => ({
      can_edit: access.can_edit,
      can_delete: access.can_delete,
      can_create: access.can_create,
      can_view: access.can_view,
      can_export: access.can_export,
      can_import: access.can_import,
    }),
    [access]
  )

  const tableParams = useMemo(
    () => ({ group_id: selectedGroupId || undefined, search: searchQuery, trash: activeTab === 'trash' ? 1 : 0 }),
    [selectedGroupId, searchQuery, activeTab]
  )

  const setSubSub = useMemo(
    () => (all: number, trashed: number) => {
      setTotalRows(all)
      setTotalTrash(trashed)
    },
    []
  )

  const handleTabChange = (index: number) => {
    setActiveTab(index === 0 ? 'all' : 'trash')
    setSearchQuery('')
    tableRef.current?.applyFilter('search', '')
    tableRef.current?.clearPage()
  }

  const handleSearch = () => tableRef.current?.applyFilter('search', searchQuery || '')
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch()
  }

  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning('Please select an action first')
      return
    }
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) {
      showWarning('Please select at least one item')
      return
    }
    setConfirmMessage(
      activeTab === 'trash' && bulkAction === 'delete'
        ? 'You are about to permanently delete these values. This action cannot be undone.'
        : 'Are you sure you want to apply this action?'
    )
    onConfirmOpen()
  }

  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || []
    if (selectedIds.length === 0) return

    setIsBulkProcessing(true)
    try {
      for (const id of selectedIds) {
        if (bulkAction === 'restore') {
          await apiClient.put(`/service-catalog/ticket-option-values/restore/${id}`)
        } else if (bulkAction === 'delete' && activeTab === 'trash') {
          await apiClient.delete(`/service-catalog/ticket-option-values/force-delete/${id}`)
        } else if (bulkAction === 'delete' && activeTab === 'all') {
          await apiClient.delete(`/service-catalog/ticket-option-values/${id}`)
        }
      }
      showSuccess('Success', selectedIds.length > 1 ? `${selectedIds.length} items updated` : 'Updated successfully')
      tableRef.current?.reload()
      setBulkAction('')
      onConfirmClose()
    } catch (error: unknown) {
      showError(error as Error)
    } finally {
      setIsBulkProcessing(false)
    }
  }

  // --- Value add/edit modal ---

  const openAddValue = () => {
    setValueForm({ ...emptyValue, sort_order: totalRows })
    setValueErrors({})
    onValueModalOpen()
  }

  const openEditValue = (row: Record<string, unknown>) => {
    setValueForm({
      id: row.id as number,
      code: String(row.code ?? ''),
      label: String(row.label ?? ''),
      sort_order: Number(row.sort_order ?? 0),
      active: row.active !== false,
    })
    setValueErrors({})
    onValueModalOpen()
  }

  const handleValueModalOpen = (rowOrId: unknown) => {
    // displayInModal passes the full row for "Edit"; openAddValue is called separately for "Add"
    if (rowOrId && typeof rowOrId === 'object') {
      openEditValue(rowOrId as Record<string, unknown>)
    }
  }

  const submitValue = async () => {
    if (!selectedGroupId) return
    setSavingValue(true)
    setValueErrors({})
    try {
      const payload = {
        group_id: selectedGroupId,
        code: valueForm.code.trim(),
        label: valueForm.label.trim(),
        sort_order: Number(valueForm.sort_order) || 0,
        active: valueForm.active,
      }
      if (valueForm.id) {
        await apiClient.put(`/service-catalog/ticket-option-values/${valueForm.id}`, payload)
        showSuccess('Success', 'Value updated successfully')
      } else {
        await apiClient.post('/service-catalog/ticket-option-values', payload)
        showSuccess('Success', 'Value created successfully')
      }
      onValueModalClose()
      tableRef.current?.reload()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors && typeof res.errors === 'object') setValueErrors(res.errors)
      else showError(err as Error)
    } finally {
      setSavingValue(false)
    }
  }

  // --- New group modal ---

  const openAddGroup = () => {
    setGroupForm(emptyGroup)
    setGroupErrors({})
    onGroupModalOpen()
  }

  const submitGroup = async () => {
    setSavingGroup(true)
    setGroupErrors({})
    try {
      const { data } = await apiClient.post('/service-catalog/ticket-option-lists', {
        group_code: groupForm.group_code.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'),
        group_label: groupForm.group_label.trim(),
        description: groupForm.description.trim() || null,
      })
      showSuccess('Success', 'Option list created successfully')
      onGroupModalClose()
      loadGroups()
      const newId = data?.data?.id ?? data?.id
      if (newId) setSelectedGroupId(Number(newId))
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors && typeof res.errors === 'object') setGroupErrors(res.errors)
      else showError(err as Error)
    } finally {
      setSavingGroup(false)
    }
  }

  return (
    <Box p={4} h="100%" overflow="hidden" display="flex" flexDirection="column">
      <VStack spacing={2} align="stretch" flex="1" overflow="hidden" minH={0}>
        <Card flexShrink={0} display="flex" flexDirection="column" flex="1" minH={0} overflow="hidden">
          <CardHeader flexShrink={0} py={3}>
            <Heading size="md" mb={1}>Ticket Option Lists</Heading>
            <Text fontSize="sm" color={colors.text}>
              Manage small dropdown / radio option sets used across ticket forms (severity, billing type, request
              source, triage questions, etc.)
            </Text>
          </CardHeader>

          <CardHeader py={2} px={5} flexShrink={0}>
            <HStack spacing={3} flexWrap="wrap">
              <Box minW="280px">
                <Select
                  size="sm"
                  value={selectedGroupId === '' ? '' : String(selectedGroupId)}
                  onChange={(e) => setSelectedGroupId(e.target.value ? Number(e.target.value) : '')}
                  placeholder={groupsLoading ? 'Loading...' : 'Select option list'}
                  isDisabled={groupsLoading}
                >
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>{g.group_label} ({g.group_code})</option>
                  ))}
                </Select>
              </Box>
              <Button size="sm" leftIcon={<Plus size={14} />} variant="outline" onClick={openAddGroup}>
                New Option List
              </Button>
              {selectedGroup?.description && (
                <Text fontSize="xs" color={colors.textMuted}>{selectedGroup.description}</Text>
              )}
            </HStack>
          </CardHeader>

          <CardHeader py={0} px={0} flexShrink={0}>
            <Box pt={2} pb={0}>
              <HStack justify="space-between" align="flex-end" spacing={4} px={4} width="100%">
                <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange} variant="enclosed">
                  <TabList borderBottom="none">
                    <Tab>
                      All <Badge ml={2} colorScheme="blue">{totalRows}</Badge>
                    </Tab>
                    <Tab>
                      Trash <Badge ml={2} colorScheme="red">{totalTrash}</Badge>
                    </Tab>
                  </TabList>
                </Tabs>
                <Box pb={2}>
                  <Button
                    size="sm"
                    colorScheme="blue"
                    leftIcon={<Plus size={14} />}
                    onClick={openAddValue}
                    isDisabled={!selectedGroupId}
                  >
                    Add Value
                  </Button>
                </Box>
              </HStack>
              <Box borderBottom="2px" borderColor={colors.border} mx={4} />
            </Box>
          </CardHeader>

          <CardHeader py={1} pt={3} pb={2} flexShrink={0} px={5}>
            <HStack spacing={4} flexWrap="wrap">
              {access.can_delete && (
                <HStack spacing={2} minW="250px">
                  <Select
                    placeholder="Bulk actions"
                    size="sm"
                    value={bulkAction}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) => setBulkAction(e.target.value)}
                  >
                    {activeTab === 'trash' && <option value="restore">Restore</option>}
                    {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                    {activeTab !== 'trash' && <option value="delete">Move to trash</option>}
                  </Select>
                  <Button size="sm" colorScheme="blue" onClick={handleBulkActionClick} isLoading={isBulkProcessing}>
                    Apply
                  </Button>
                </HStack>
              )}
              <Box flex="0 1 auto" minW="250px" maxW="400px">
                <InputGroup size="sm">
                  <Input
                    ref={searchRef}
                    placeholder="Search by code, label..."
                    value={searchQuery}
                    onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    pr="2.5rem"
                  />
                  <InputRightElement width="2.5rem">
                    <IconButton
                      aria-label="Search"
                      icon={<Search size={16} />}
                      size="sm"
                      h="1.75rem"
                      w="1.75rem"
                      onClick={handleSearch}
                      variant="ghost"
                    />
                  </InputRightElement>
                </InputGroup>
              </Box>
            </HStack>
          </CardHeader>

          <CardBody p={0} pt={1} pb={4} mx={5} flex="1" overflow="hidden" display="flex" flexDirection="column" minH={0}>
            <Box h="100%" display="flex" flexDirection="column" minH={0}>
              {selectedGroupId ? (
                <DataTable
                  ref={tableRef}
                  options={tableOptions}
                  params={tableParams}
                  setSubSub={setSubSub}
                  access={permissions}
                  onOpenModal={handleValueModalOpen}
                />
              ) : (
                <Text color={colors.textMuted} p={4}>Select an option list above to manage its values.</Text>
              )}
            </Box>
          </CardBody>
        </Card>
      </VStack>

      {/* Confirm bulk action */}
      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirm</ModalHeader>
          <ModalBody>{confirmMessage}</ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onConfirmClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleConfirmBulkAction} isLoading={isBulkProcessing}>Confirm</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add/Edit value modal */}
      <Modal isOpen={isValueModalOpen} onClose={onValueModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{valueForm.id ? 'Edit Value' : 'Add Value'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={!!valueErrors.code?.length} isRequired>
                <FormLabel>Code</FormLabel>
                <Input
                  value={valueForm.code}
                  onChange={(e) => setValueForm((p) => ({ ...p, code: e.target.value }))}
                  placeholder="e.g. 4, true, Yes"
                />
                <FormHelperText>The value actually submitted with the ticket — keep it stable once in use.</FormHelperText>
                <FormErrorMessage>{valueErrors.code?.[0]}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!valueErrors.label?.length} isRequired>
                <FormLabel>Label</FormLabel>
                <Input
                  value={valueForm.label}
                  onChange={(e) => setValueForm((p) => ({ ...p, label: e.target.value }))}
                  placeholder="e.g. Severity 4, Billable, Yes"
                />
                <FormErrorMessage>{valueErrors.label?.[0]}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!valueErrors.sort_order?.length}>
                <FormLabel>Sort order</FormLabel>
                <Input
                  type="number"
                  min={0}
                  value={valueForm.sort_order}
                  onChange={(e) => setValueForm((p) => ({ ...p, sort_order: Number(e.target.value) }))}
                />
                <FormErrorMessage>{valueErrors.sort_order?.[0]}</FormErrorMessage>
              </FormControl>
              <Checkbox
                isChecked={valueForm.active}
                onChange={(e) => setValueForm((p) => ({ ...p, active: e.target.checked }))}
                colorScheme="blue"
              >
                Active (shown in ticket forms)
              </Checkbox>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onValueModalClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={submitValue} isLoading={savingValue}>
              {valueForm.id ? 'Save' : 'Add Value'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* New option list (group) modal */}
      <Modal isOpen={isGroupModalOpen} onClose={onGroupModalClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>New Option List</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isInvalid={!!groupErrors.group_label?.length} isRequired>
                <FormLabel>Display name</FormLabel>
                <Input
                  value={groupForm.group_label}
                  onChange={(e) => setGroupForm((p) => ({ ...p, group_label: e.target.value }))}
                  placeholder="e.g. Escalation Reason"
                />
                <FormErrorMessage>{groupErrors.group_label?.[0]}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!groupErrors.group_code?.length} isRequired>
                <FormLabel>Group code</FormLabel>
                <Input
                  value={groupForm.group_code}
                  onChange={(e) => setGroupForm((p) => ({ ...p, group_code: e.target.value }))}
                  placeholder="e.g. escalation_reason"
                />
                <FormHelperText>
                  Lowercase, letters/numbers/underscores only. This is the identifier a developer wires into a form
                  via <code>/options/ticket-option-lists/{'{group_code}'}</code> — cannot be changed later.
                </FormHelperText>
                <FormErrorMessage>{groupErrors.group_code?.[0]}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={!!groupErrors.description?.length}>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={groupForm.description}
                  onChange={(e) => setGroupForm((p) => ({ ...p, description: e.target.value }))}
                  rows={2}
                />
                <FormErrorMessage>{groupErrors.description?.[0]}</FormErrorMessage>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onGroupModalClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={submitGroup} isLoading={savingGroup}>Create</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}
